import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAgentSession } from '../../hooks/useAgentSession';
import { supabase } from '../../lib/supabase';
import Layout from '../Layout';
import PhaseIndicator from './PhaseIndicator';
import ChatPanel from './ChatPanel';
import ResumePreviewPanel from './ResumePreviewPanel';
import SessionRecovery from './SessionRecovery';
import { Bot, PanelRightOpen, PanelRightClose, Download } from 'lucide-react';
import { exportAsWord, exportAsTextFile } from '../../lib/exportResume';
import type { FinalResume } from '../../types/resume';

interface PreviousSession {
  id: string;
  status: string;
  current_phase: string;
  updated_at: string;
}

export default function CoachPage() {
  const { user } = useAuth();
  const agent = useAgentSession();
  const [showPreview, setShowPreview] = useState(true);
  const [previousSessions, setPreviousSessions] = useState<PreviousSession[]>([]);
  const [loading, setLoading] = useState(true);

  // Load previous sessions
  useEffect(() => {
    if (!user) return;

    async function loadSessions() {
      const { data } = await supabase
        .from('coach_sessions')
        .select('id, status, current_phase, updated_at')
        .eq('user_id', user!.id)
        .in('status', ['active', 'paused'])
        .order('updated_at', { ascending: false })
        .limit(5);

      setPreviousSessions(data ?? []);
      setLoading(false);
    }

    loadSessions();
  }, [user]);

  const handleNewSession = useCallback(async () => {
    try {
      // Load user's master resume if they have one
      const { data: resumes } = await supabase
        .from('master_resumes')
        .select('id')
        .eq('user_id', user!.id)
        .order('updated_at', { ascending: false })
        .limit(1);

      const masterResumeId = resumes?.[0]?.id;
      await agent.createSession(masterResumeId);

      // Send initial greeting — context differs based on whether a resume exists
      const greeting = masterResumeId
        ? "Hello! I'm ready to work on a tailored resume. Let's get started."
        : "Hello! I'd like to create a tailored resume. I don't have one on file yet.";
      await agent.sendMessage(greeting);
    } catch (error) {
      console.error('Failed to create session:', error);
    }
  }, [user, agent]);

  const handleResumeSession = useCallback(async (sessionId: string) => {
    try {
      await agent.resumeSession(sessionId);
      await agent.sendMessage("I'm back! Where were we?");
    } catch (error) {
      console.error('Failed to resume session:', error);
    }
  }, [agent]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </Layout>
    );
  }

  // No active session — show start screen
  if (!agent.sessionId) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Bot className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">AI Resume Coach</h1>
            <p className="text-gray-500 max-w-md mx-auto">
              Your personal resume coach that researches companies, interviews you for the details that matter, and writes a resume that gets interviews.
            </p>
          </div>

          {previousSessions.length > 0 && (
            <SessionRecovery
              sessions={previousSessions}
              onResume={handleResumeSession}
              onNewSession={handleNewSession}
            />
          )}

          <button
            onClick={handleNewSession}
            className="w-full py-4 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors text-lg"
          >
            Start a New Coaching Session
          </button>

          <div className="mt-8 grid grid-cols-3 gap-4 text-center">
            <div className="p-4">
              <p className="text-2xl font-bold text-blue-600">~15 min</p>
              <p className="text-xs text-gray-500 mt-1">Average session</p>
            </div>
            <div className="p-4">
              <p className="text-2xl font-bold text-blue-600">$0.43</p>
              <p className="text-xs text-gray-500 mt-1">Per session</p>
            </div>
            <div className="p-4">
              <p className="text-2xl font-bold text-blue-600">90%+</p>
              <p className="text-xs text-gray-500 mt-1">ATS score target</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Active session — show coach interface
  return (
    <Layout>
      <div className="flex flex-col h-[calc(100vh-4rem)]">
        {/* Phase indicator */}
        <PhaseIndicator currentPhase={agent.phase} />

        {/* Main content — split layout */}
        <div className="flex flex-1 overflow-hidden">
          {/* Chat panel */}
          <div className={`flex flex-col ${showPreview ? 'w-1/2' : 'flex-1'} border-r border-gray-200 transition-all`}>
            <ChatPanel
              messages={agent.messages}
              toolStatuses={agent.toolStatuses}
              askUserPrompt={agent.askUserPrompt}
              isProcessing={agent.isProcessing}
              isConnected={agent.isConnected}
              error={agent.error}
              onSendMessage={agent.sendMessage}
            />
          </div>

          {/* Resume preview panel */}
          {showPreview && (
            <div className="w-1/2 bg-gray-50">
              <ResumePreviewPanel updates={agent.resumeUpdates} />
            </div>
          )}
        </div>

        {/* Export download bar */}
        {agent.exportData && (
          <div className="border-t border-gray-200 bg-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Download className="w-4 h-4 text-green-600" />
              <span className="font-medium">Your resume is ready!</span>
              {agent.exportData.resume.ats_score && (
                <span className="text-gray-500">ATS Score: {agent.exportData.resume.ats_score}%</span>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  const r = agent.exportData!.resume;
                  const finalResume: FinalResume = {
                    summary: r.summary,
                    experience: r.experience.map(e => ({
                      ...e,
                      end_date: e.end_date || null,
                      bullets: e.bullets.map(b => ({ text: b.text, source: b.source })),
                    })),
                    skills: r.skills,
                    education: r.education.map(e => ({ institution: e.institution, degree: e.degree, field: e.field, year: e.year })),
                    certifications: r.certifications.map(c => ({ name: c.name, issuer: c.issuer, year: c.year })),
                    ats_score: r.ats_score,
                  };
                  exportAsWord(finalResume, 'tailored-resume.doc');
                }}
                className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
              >
                Download Word
              </button>
              <button
                onClick={() => {
                  const r = agent.exportData!.resume;
                  const finalResume: FinalResume = {
                    summary: r.summary,
                    experience: r.experience.map(e => ({
                      ...e,
                      end_date: e.end_date || null,
                      bullets: e.bullets.map(b => ({ text: b.text, source: b.source })),
                    })),
                    skills: r.skills,
                    education: r.education.map(e => ({ institution: e.institution, degree: e.degree, field: e.field, year: e.year })),
                    certifications: r.certifications.map(c => ({ name: c.name, issuer: c.issuer, year: c.year })),
                    ats_score: r.ats_score,
                  };
                  exportAsTextFile(finalResume, 'tailored-resume.txt');
                }}
                className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors"
              >
                Download Text
              </button>
            </div>
          </div>
        )}

        {/* Toggle preview button */}
        <button
          onClick={() => setShowPreview(!showPreview)}
          className="absolute right-4 bottom-20 p-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 transition-colors z-10"
          title={showPreview ? 'Hide preview' : 'Show preview'}
        >
          {showPreview ? (
            <PanelRightClose className="w-4 h-4 text-gray-500" />
          ) : (
            <PanelRightOpen className="w-4 h-4 text-gray-500" />
          )}
        </button>
      </div>
    </Layout>
  );
}
